import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PAGE_META } from '../lib/seoMeta';
import { Calculator, ArrowRight, Phone } from 'lucide-react';
import { Button } from './ui/button';
import Breadcrumbs from './Breadcrumbs';

const ROOM_TYPES = [
  { id: 'badkamer', label: 'Badkamer',
    prices: { standaard: [15000, 22000], premium: [22000, 32000], luxe: [32000, 50000] } },
  { id: 'keuken', label: 'Keuken',
    prices: { standaard: [15000, 25000], premium: [25000, 40000], luxe: [40000, 65000] } },
  { id: 'woonkamer', label: 'Woonkamer',
    prices: { standaard: [8000, 15000], premium: [15000, 22000], luxe: [22000, 35000] } },
  { id: 'slaapkamer', label: 'Slaapkamer',
    prices: { standaard: [5000, 10000], premium: [10000, 15000], luxe: [15000, 25000] } },
  { id: 'hal', label: 'Hal / Inkom',
    prices: { standaard: [3000, 6000], premium: [6000, 10000], luxe: [10000, 18000] } },
  { id: 'zolder', label: 'Zolder',
    prices: { standaard: [8000, 15000], premium: [15000, 25000], luxe: [25000, 40000] } },
];

const QUALITY = [
  { id: 'standaard', label: 'Standaard afwerking',
    desc: 'Fatsoenlijke afwerking met kwaliteitsmaterialen. Vast sanitair, standaard kraanwerk, moderne tegels.' },
  { id: 'premium', label: 'Premium afwerking',
    desc: 'Gekleurd kraanwerk, speciale spiegel, hoogwaardige tegels, meer maatwerk en afwerkingsdetails.' },
  { id: 'luxe', label: 'Luxe afwerking',
    desc: 'Natuursteen, inbouwkraanwerk, speciale wastafel of bad, designverlichting en volledig maatwerk.' },
];

const RenovatieCalculator = () => {
  const [rooms, setRooms] = useState([]);
  const [quality, setQuality] = useState('standaard');
  const [calculated, setCalculated] = useState(false);

  const toggleRoom = (id) => {
    setRooms((prev) => prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]);
    setCalculated(false);
  };

  const calcMin = () => {
    return rooms.reduce((sum, id) => {
      const r = ROOM_TYPES.find((rt) => rt.id === id);
      return sum + (r ? r.prices[quality][0] : 0);
    }, 0);
  };

  const calcMax = () => {
    return rooms.reduce((sum, id) => {
      const r = ROOM_TYPES.find((rt) => rt.id === id);
      return sum + (r ? r.prices[quality][1] : 0);
    }, 0);
  };

  const handleCalculate = () => setCalculated(true);

  // Build /start URL with prefill params so lead form is prefilled
  const startPrefillUrl = () => {
    const params = new URLSearchParams();
    if (rooms.length) params.set('rooms', rooms.join(','));
    if (quality) params.set('quality', quality);
    if (calculated) params.set('estimate', calcMin() + '-' + calcMax());
    return '/start?' + params.toString();
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "Wat kost een badkamer renovatie in Belgie?", "acceptedAnswer": { "@type": "Answer", "text": "Een badkamerrenovatie bij Max Q kost gemiddeld 15.000 tot 22.000 euro voor standaard afwerking met kwaliteitsmaterialen. Premium afwerking (gekleurd kraanwerk, specialere spiegel) loopt van 22.000 tot 32.000 euro. Luxe afwerking met natuursteen, inbouwkraanwerk en speciaal sanitair start vanaf 32.000 euro." }},
      { "@type": "Question", "name": "Wat kost een keuken renovatie?", "acceptedAnswer": { "@type": "Answer", "text": "Een keukenrenovatie kost tussen 15.000 en 25.000 euro voor standaard afwerking. Premium keukens met kookeiland en hoogwaardige apparatuur lopen van 25.000 tot 40.000 euro. Luxe maatwerk-keukens vanaf 40.000 euro." }},
      { "@type": "Question", "name": "Wat kost een totaalrenovatie per m2?", "acceptedAnswer": { "@type": "Answer", "text": "Een totaalrenovatie kost gemiddeld 800 tot 1.500 euro per vierkante meter in Belgie, afhankelijk van de staat van de woning en het afwerkingsniveau." }},
    ]
  };

  return (
    <div data-testid="calculator-page">
      <Helmet>
        <title>{PAGE_META.calculator.title}</title>
        <meta name="description" content={PAGE_META.calculator.description} />
        <link rel="canonical" href="https://maxq.be/calculator" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Prijscalculator' }]} />

      <section className="bg-gradient-to-br from-[#3a190b] to-[#500000] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Calculator className="h-12 w-12 mx-auto mb-4 opacity-80" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="calculator-title">Renovatie Prijscalculator</h1>
          <p className="text-lg text-white/80">Bereken gratis een indicatie van uw renovatiekosten</p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <div className="mb-10">
            <h2 className="text-xl font-bold mb-4">1. Welke ruimtes wilt u renoveren?</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {ROOM_TYPES.map((room) => {
                const active = rooms.includes(room.id);
                return (
                  <button
                    key={room.id}
                    onClick={() => toggleRoom(room.id)}
                    className={
                      active
                        ? 'bg-[#3a190b] text-white border-2 border-[#3a190b] rounded-lg p-4 text-sm font-medium transition-all'
                        : 'bg-white text-[#202020] border-2 border-gray-200 rounded-lg p-4 text-sm font-medium hover:border-[#3a190b] transition-all'
                    }
                    data-testid={'room-' + room.id}
                  >
                    {room.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-10">
            <h2 className="text-xl font-bold mb-4">2. Afwerkingsniveau?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {QUALITY.map((q) => (
                <button
                  key={q.id}
                  onClick={() => { setQuality(q.id); setCalculated(false); }}
                  className={
                    quality === q.id
                      ? 'text-left bg-[#3a190b] text-white border-2 border-[#3a190b] rounded-lg p-4 transition-all'
                      : 'text-left bg-white text-[#202020] border-2 border-gray-200 rounded-lg p-4 hover:border-[#3a190b] transition-all'
                  }
                  data-testid={'quality-' + q.id}
                >
                  <div className="font-semibold text-sm mb-1">{q.label}</div>
                  <div className={quality === q.id ? 'text-xs text-white/85 leading-snug' : 'text-xs text-[#202020]/60 leading-snug'}>
                    {q.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleCalculate}
            disabled={rooms.length === 0}
            className="w-full bg-[#3a190b] text-white py-4 rounded-lg font-semibold text-lg hover:bg-[#500000] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="calculate-btn"
          >
            Bereken indicatie
          </button>

          {calculated && rooms.length > 0 && (
            <div className="mt-8 bg-gray-50 rounded-xl p-8 text-center" data-testid="calculator-result">
              <p className="text-sm text-gray-500 mb-2">Geschatte kosten (incl. BTW 21%)</p>
              <p className="text-4xl font-bold text-[#3a190b] mb-1">
                {calcMin().toLocaleString('nl-BE')} - {calcMax().toLocaleString('nl-BE')} EUR
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Dit is een indicatie. De exacte prijs hangt af van uw specifieke situatie en materiaalkeuze.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to={startPrefillUrl()}>
                  <Button className="bg-[#3a190b] text-white hover:bg-[#500000] px-6 py-3" data-testid="calculator-cta">
                    Direct offerte aanvragen <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <a href="tel:+32488152028">
                  <Button variant="outline" className="border-[#3a190b] text-[#3a190b] px-6 py-3">
                    <Phone className="h-4 w-4 mr-2" /> Bel ons
                  </Button>
                </a>
              </div>
              <p className="text-xs text-gray-500 mt-4">Uw selectie wordt automatisch meegenomen naar de offerte aanvraag.</p>
            </div>
          )}
        </div>
      </section>

      <section className="py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-6">Veelgestelde vragen over renovatiekosten</h2>
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-6">
              <h3 className="font-semibold mb-2">Wat kost een badkamer renovatie in Belgie?</h3>
              <p className="text-gray-600 text-sm">Een <Link to="/diensten/badkamer-renoveren" className="text-[#3a190b] underline">badkamerrenovatie</Link> bij Max Q kost gemiddeld 15.000 tot 22.000 euro voor standaard afwerking. Met premium materialen zoals gekleurd kraanwerk en speciale spiegel loopt dit van 22.000 tot 32.000 euro. Luxe afwerking met natuursteen en inbouwkraanwerk start vanaf 32.000 euro.</p>
            </div>
            <div className="bg-white rounded-lg p-6">
              <h3 className="font-semibold mb-2">Wat kost een keuken renovatie?</h3>
              <p className="text-gray-600 text-sm">Een <Link to="/diensten/keuken-renoveren" className="text-[#3a190b] underline">keukenrenovatie</Link> varieert van 15.000 tot 25.000 euro voor standaard afwerking. Kookeiland en premium apparatuur verhogen de prijs tot 40.000 euro, luxe maatwerk-keukens vanaf 40.000 euro.</p>
            </div>
            <div className="bg-white rounded-lg p-6">
              <h3 className="font-semibold mb-2">Wat kost een totaalrenovatie?</h3>
              <p className="text-gray-600 text-sm">Een <Link to="/diensten/totaalrenovatie" className="text-[#3a190b] underline">totaalrenovatie</Link> kost gemiddeld 800 tot 1.500 euro per m2. Voor een woning van 150m2 komt dit neer op 120.000 tot 225.000 euro.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RenovatieCalculator;
